import {FC, useContext, useEffect, useMemo} from 'react';
import Icon from 'components/Icon';
import {Typography} from 'components/Typography';
import {showModal} from 'modules/modals/thunks';
import {MODALS} from 'modules/modals/constants';
import {getWineshopsFoundCount, isBySimple} from 'modules/wineshops-list/helpers';
import {ProductPageContext} from 'modules/product-card/context';
import style from './style.module.scss';
import {useAppDispatch} from 'core/redux/hooks/useAppDispatch';
import {useAppSelector} from 'core/redux/hooks/useAppSelector';
import {selectProductData} from 'modules/product-card/selectors';
import {useGetUserQuery} from 'modules/user/services';
import {useExpressOrderGtag} from 'hooks/analytics/gtag/catalog/useExpressOrderGtag';

interface IShopTitleProps {
    title: string;
    onClick: () => void;
    isButton?: boolean;
}

const ShopTitle: FC<IShopTitleProps> = ({title, onClick, isButton}) => {
    if (isButton) {
        return (
            <button className={style.button} onClick={onClick} type={'button'}>
                <Typography variant={'label-14-m'}>{title}</Typography>
            </button>
        );
    }

    return (
        <Typography className={style.button} variant={'label-14-m'} onClick={onClick}>
            {title}
        </Typography>
    );
};

export const ProductReceivingMethodToday: FC = () => {
    const {expressWineshop, wineshopsForTodayWithStock, setIsAddToCartWhenReserving} = useContext(ProductPageContext);
    const dispatch = useAppDispatch();
    const {data: user} = useGetUserQuery();
    const productData = useAppSelector(selectProductData);
    const {sendShowWineryPopupEvent, sendClickWineryPopupEvent} = useExpressOrderGtag();

    const defaultTitle = useMemo(
        () => getWineshopsFoundCount(wineshopsForTodayWithStock.length, ['винотеки', 'винотек', 'винотек']),
        [wineshopsForTodayWithStock.length],
    );

    const userShopTitle = useMemo(() => {
        if (!user) {
            return null;
        }
        return wineshopsForTodayWithStock.find(({id}) => Number(id) === user.expressOrderStoreId)?.title;
    }, [user, wineshopsForTodayWithStock]);

    const selectedWineShop = useMemo(() => {
        if (!expressWineshop) {
            return null;
        }
        return wineshopsForTodayWithStock.find((wineshopForToday) => {
            const warehouseCode = expressWineshop.warehouseCode;
            return warehouseCode && Number(wineshopForToday.warehouseCode) === Number(warehouseCode);
        });
    }, [expressWineshop, wineshopsForTodayWithStock]);

    const title = userShopTitle || defaultTitle;
    const displayTitle = selectedWineShop?.title || title;
    const isReserveOnly = !productData?.canBuy && productData?.canReserve;

    const isReserveOnlyInBySimpleShops = useMemo(
        () =>
            0 < wineshopsForTodayWithStock.length &&
            wineshopsForTodayWithStock.every((wineshop) => wineshop.stockTypes.some(({code}) => isBySimple(code))),
        [wineshopsForTodayWithStock],
    );

    const handleOpenMap = () => {
        sendClickWineryPopupEvent();

        if (!productData?.canBuy && productData?.canReserve) {
            setIsAddToCartWhenReserving?.(true);
        }
        dispatch(showModal(MODALS.PRODUCT_PICKUP.name)).then(() => {
            sendShowWineryPopupEvent('pdp');
        });
    };

    useEffect(() => {
        return () => {
            setIsAddToCartWhenReserving?.(false);
        };
    }, [setIsAddToCartWhenReserving]);

    return (
        <div className={style.main} data-skeleton-item={true}>
            <Icon className={style.icon} icon={'lightning'} />
            <div className={style.text}>
                <Typography variant={'label-14-r'}>
                    Забрать сегодня из{' '}
                    <ShopTitle title={displayTitle} onClick={handleOpenMap} isButton={!selectedWineShop?.title} />
                </Typography>
                {isReserveOnly && (
                    <Typography color={'gray60'} variant={'label-14-r'}>
                        {isReserveOnlyInBySimpleShops
                            ? 'Товар хранится в винотеке и забрать его можно только там.'
                            : 'Товар хранится в винотеке и забрать его можно только там. Срок хранения 3 дня.'}
                    </Typography>
                )}
            </div>
        </div>
    );
};
